import { useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import { PUBLICATION_STATUSES, type Publication, type PublicationStatus } from '../types';
import { useUpdatePublication } from '../hooks';
import { DmsImage } from './DmsImage';

interface Props {
  publications: Publication[];
  onCardClick?: (p: Publication) => void;
}

export function PublicationKanbanView({ publications, onCardClick }: Props) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const updateMutation = useUpdatePublication();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const grouped = useMemo(() => {
    const map: Record<PublicationStatus, Publication[]> = {
      'Uncategorized': [],
      'Ideation': [],
      'In Progress': [],
      'To Review': [],
      'Approuved': [],
      'Scheduled': [],
      'Published': [],
    };
    for (const p of publications) {
      map[p.status]?.push(p);
    }
    return map;
  }, [publications]);

  const active = activeId ? publications.find((p) => p.id === activeId) : null;

  const handleDragStart = (e: DragStartEvent) => setActiveId(String(e.active.id));

  const handleDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    if (!e.over) return;
    const newStatus = String(e.over.id) as PublicationStatus;
    if (!PUBLICATION_STATUSES.includes(newStatus)) return;
    const pub = publications.find((p) => p.id === e.active.id);
    if (!pub || pub.status === newStatus) return;
    updateMutation.mutate({ id: pub.id, data: { status: newStatus } });
  };

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="flex gap-3 overflow-x-auto pb-2">
        {PUBLICATION_STATUSES.map((status) => (
          <KanbanColumn
            key={status}
            status={status}
            items={grouped[status]}
            onCardClick={onCardClick}
          />
        ))}
      </div>
      <DragOverlay>
        {active ? <KanbanCard publication={active} /> : null}
      </DragOverlay>
    </DndContext>
  );
}

function KanbanColumn({
  status,
  items,
  onCardClick,
}: {
  status: PublicationStatus;
  items: Publication[];
  onCardClick?: (p: Publication) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  return (
    <div
      ref={setNodeRef}
      className={`flex-shrink-0 w-72 bg-slate-50 rounded-lg p-2 ${isOver ? 'ring-2 ring-blue-400' : ''}`}
    >
      <div className="flex items-center justify-between px-2 py-1 mb-2">
        <h3 className="text-sm font-semibold text-slate-800">{status}</h3>
        <span className="text-xs text-slate-500">{items.length}</span>
      </div>
      <div className="flex flex-col gap-2 min-h-[120px]">
        {items.map((p) => (
          <DraggableCard key={p.id} publication={p} onClick={() => onCardClick?.(p)} />
        ))}
      </div>
    </div>
  );
}

function DraggableCard({
  publication,
  onClick,
}: {
  publication: Publication;
  onClick?: () => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: publication.id });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={onClick}
      className={`cursor-grab ${isDragging ? 'opacity-50' : ''}`}
    >
      <KanbanCard publication={publication} />
    </div>
  );
}

function KanbanCard({ publication }: { publication: Publication }) {
  const title = publication.titleEn || publication.titleFr || '(untitled)';
  const synopsis = publication.bodyEn || publication.bodyFr || '';
  const visualId = publication.visualEnDmsIds[0] || publication.visualFrDmsIds[0] || null;
  return (
    <div className="bg-white rounded-md border border-slate-200 shadow-sm hover:shadow transition-shadow overflow-hidden flex flex-col h-[500px]">
      {/* Photo area — uniform 300px; placeholder when empty keeps heights aligned */}
      <div className="h-[300px] shrink-0 bg-slate-100 border-b border-slate-200">
        {visualId ? (
          <DmsImage
            documentId={visualId}
            alt={title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[10px] uppercase tracking-wide text-slate-400">
            No image
          </div>
        )}
      </div>
      <div className="p-2.5 flex flex-col flex-1 min-h-0">
        <div className="text-xs font-medium text-slate-900 line-clamp-2 min-h-[2.2em]">{title}</div>
        <div className="text-[11px] text-slate-600 line-clamp-3 mt-1 flex-1">
          {synopsis || <span className="text-slate-400 italic">No synopsis</span>}
        </div>
        {publication.channels.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {publication.channels.slice(0, 3).map((c) => (
              <span key={c} className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded">
                {c}
              </span>
            ))}
            {publication.channels.length > 3 && (
              <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded">
                +{publication.channels.length - 3}
              </span>
            )}
          </div>
        )}
        {publication.publishDate && (
          <div className="text-[10px] text-slate-500 mt-1.5">
            {new Date(publication.publishDate).toISOString().slice(0, 10)}
          </div>
        )}
      </div>
    </div>
  );
}
